/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For mor information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.dashboard.sidebar",
  title: "Dashboard Sidebar",
  description: "Dashboard wrapper for ua.appbar that applies role-scoped client-side navigation caching. This is a UX optimization only and must never be used as an authorization boundary.",
  fields: {
    navigation: {
      title: "Navigation",
      type: "object",
      description: "Role-filtered navigation model from dashboard.registry.buildNavigation()."
    },
    cacheKey: {
      title: "Cache Key",
      type: "string",
      description: "Role signature key used for client-side nav cache scope."
    },
    cacheVersion: {
      title: "Cache Version",
      type: "string",
      description: "Nav cache version token. Bump this to invalidate stale client cache.",
      default: "1"
    },
    cacheStorage: {
      title: "Cache Storage",
      type: "string",
      description: "Browser storage type for nav cache.",
      default: "session",
      list: {
        must: ["session", "local"]
      }
    },
    class: {
      title: "Class",
      type: "class",
      description: "Additional class list for the ua.appbar wrapper."
    },
    sidebarClass: {
      title: "Sidebar Class",
      type: "class",
      description: "Additional class list for the sidebar container."
    },
    contentClass: {
      title: "Content Class",
      type: "class",
      description: "Additional class list for the content container."
    },
    sidebarAttributes: {
      title: "Sidebar Attributes",
      type: "object",
      description: "Additional attributes for the sidebar container."
    },
    contentAttributes: {
      title: "Content Attributes",
      type: "object",
      description: "Additional attributes for the content container."
    },
    hideSpacer: {
      title: "Hide Spacer",
      type: "boolean",
      description: "Hide the top spacer in the sidebar.",
      default: true
    },
    mobileMenu: {
      title: "Mobile Menu",
      type: "boolean",
      description: "Enable mobile menu button rendering in ua.appbar.",
      default: false
    },
    singleOpen: {
      title: "Single Open",
      type: "boolean",
      description: "Allow only one open section at a time.",
      default: true
    },
    rememberOpenSection: {
      title: "Remember Open Section",
      type: "boolean",
      description: "Persist open section selection by path.",
      default: true
    },
    openSectionStorageKey: {
      title: "Open Section Storage Key",
      type: "string",
      description: "Storage key used by ua.appbar for remembering open section.",
      default: "dashboard.sidebar.open.section"
    },
    header: {
      title: "Header",
      type: "inner",
      description: "Header content rendered at the top of the sidebar."
    },
    footer: {
      title: "Footer",
      type: "inner",
      description: "Footer content rendered at the bottom of the sidebar."
    },
    inner: {
      title: "Inner",
      type: "inner",
      description: "Main dashboard content rendered beside the sidebar.",
      required: true
    }
  }
});

function toArray(value) {
  if (value instanceof Array) return value;
  if (value === undefined || value === null || value === "") return [];
  return [value];
}

function navIconName(icon) {
  var raw = (icon || "bi-grid").toString().trim();
  if (!raw) return "grid";
  if (raw.indexOf("bi ") === 0) {
    var parts = raw.split(/\s+/);
    raw = parts[parts.length - 1] || "bi-grid";
  }
  if (raw.indexOf("bi-") === 0) return raw.substring(3);
  return raw;
}

function createSideNavItem(item, extraClass) {
  var className = (extraClass || "dashboard-link").toString();
  if ((item || {}).active) className += " active";
  return {
    n: "ua.navbar.item",
    src: (item && item.href) || "#",
    title: (item && item.title) || "",
    icon: navIconName((item && item.icon) || "bi-grid"),
    class: className,
    type: "icon"
  };
}

function createGroupNode(group, depth, forceOpen) {
  var level = depth || 1;
  var row = group || {};
  var icon = navIconName(row.icon || "bi-grid");
  var childGroups = toArray(row.groups);
  var childLinks = toArray(row.items);
  var children = [];

  for (var i = 0; i < childLinks.length; i++) {
    children.push(createSideNavItem(childLinks[i], "dashboard-link dashboard-link-child dashboard-link-depth-" + level));
  }

  for (var g = 0; g < childGroups.length; g++) {
    children.push(createGroupNode(childGroups[g], level + 1, forceOpen));
  }

  if (children.length === 0) {
    return {
      n: "div",
      c: "dashboard-nav-group-empty",
      i: row.title || ""
    };
  }

  return {
    n: "details",
    c: "dashboard-nav-group dashboard-nav-group-depth-" + level + (row.active ? " is-active" : ""),
    open: forceOpen === true ? true : (row.active === true),
    i: [
      {
        n: "summary",
        c: "dashboard-nav-group-summary",
        i: [
          {
            n: "span",
            c: "dashboard-nav-group-summary-left",
            i: [
              { n: "i", c: "bi bi-" + (icon || "grid") },
              { n: "span", i: row.title || "Group" }
            ]
          },
          { n: "i", c: "bi bi-chevron-down dashboard-nav-group-chevron" }
        ]
      },
      {
        n: "div",
        c: "dashboard-nav-group-items",
        i: children
      }
    ]
  };
}

function toMobileMenuItem(node) {
  var row = node || {};
  var childRows = toArray(row.children);
  var hasChildren = childRows.length > 0;

  var item = {
    n: "ua.navbar.item",
    src: hasChildren ? "#" : (row.href || "#"),
    title: row.title || "",
    icon: navIconName(row.icon || "bi-grid"),
    type: "icon",
    class: "ua-app-bar-menu-item" + (row.active ? " active" : "")
  };

  if (hasChildren) {
    item.menu = {
      type: "halfscreen",
      inner: childRows.map(function(child) {
        return toMobileMenuItem(child);
      })
    };
  }

  return item;
}

function resolveStorage(options) {
  if (typeof window === "undefined") return null;
  try {
    if ((options.cacheStorage || "session").toString().toLowerCase() === "local") {
      return window.localStorage;
    }
  } catch (error) {}

  try {
    return window.sessionStorage;
  } catch (error2) {
    return null;
  }
}

function getCacheStorageKey(options) {
  var roleKey = (options.cacheKey || "").toString().trim() || "anonymous";
  return "ua.dashboard.sidebar.nav:" + roleKey;
}

function readCachedNavigation(options) {
  var storage = resolveStorage(options);
  if (!storage) return null;

  var key = getCacheStorageKey(options);
  try {
    var raw = storage.getItem(key);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if ((parsed.version || "") !== (options.cacheVersion || "1").toString()) return null;
    if (!parsed.navigation || typeof parsed.navigation !== "object") return null;
    return parsed.navigation;
  } catch (error) {
    return null;
  }
}

function writeCachedNavigation(options, navigation) {
  if (!(navigation) || typeof navigation !== "object") return;

  var storage = resolveStorage(options);
  if (!storage) return;

  var key = getCacheStorageKey(options);
  try {
    storage.setItem(key, JSON.stringify({
      version: (options.cacheVersion || "1").toString(),
      navigation: navigation
    }));
  } catch (error) {}
}

function buildAppbarOptions(options, navigation) {
  var nav = (navigation && typeof navigation === "object") ? navigation : {};
  var navOverview = nav.overview || null;
  var navContext = nav.contextSection || null;
  var navSections = toArray(nav.sections);
  var navMobileMenuItems = toArray(nav.mobileMenuItems);

  var appBarItems = [];
  var appBarTailLinks = [];
  if (navOverview) {
    appBarItems.push(createSideNavItem(navOverview, "dashboard-link dashboard-link-overview"));
  }

  var appBarSections = [];
  if (navContext && navContext.asLink === true) {
    var contextLink = toArray(navContext.directItems)[0] || toArray(navContext.items)[0] || null;
    if (contextLink) {
      appBarTailLinks.push(createSideNavItem(contextLink, "dashboard-link dashboard-link-tail"));
    }
  } else if (navContext && (toArray(navContext.groups).length > 0 || toArray(navContext.directItems).length > 0)) {
    var contextItems = [];
    var contextDirect = toArray(navContext.directItems);
    for (var cd = 0; cd < contextDirect.length; cd++) {
      contextItems.push(createSideNavItem(contextDirect[cd], "dashboard-link dashboard-link-child"));
    }
    for (var cg = 0; cg < toArray(navContext.groups).length; cg++) {
      contextItems.push(createGroupNode(toArray(navContext.groups)[cg], 1, true));
    }

    appBarSections.push({
      id: navContext.id || "context",
      title: navContext.title,
      icon: navIconName(navContext.icon),
      open: true,
      spacerBefore: navContext.spacerBefore === true,
      spacerAfter: navContext.spacerAfter === true,
      class: "dashboard-nav-section dashboard-nav-section-context",
      items: contextItems
    });
  }

  for (var s = 0; s < navSections.length; s++) {
    var section = navSections[s];
    if (navContext && navContext.id === section.id) continue;
    if (toArray(section.groups).length === 0 && toArray(section.directItems).length === 0) continue;

    if (section.asLink === true) {
      var linkRow = toArray(section.directItems)[0] || toArray(section.items)[0] || null;
      if (linkRow) {
        appBarTailLinks.push(createSideNavItem(linkRow, "dashboard-link dashboard-link-tail"));
      }
      continue;
    }

    var sectionItems = [];
    var sectionDirect = toArray(section.directItems);
    for (var sd = 0; sd < sectionDirect.length; sd++) {
      sectionItems.push(createSideNavItem(sectionDirect[sd], "dashboard-link dashboard-link-child"));
    }
    for (var sg = 0; sg < toArray(section.groups).length; sg++) {
      sectionItems.push(createGroupNode(toArray(section.groups)[sg], 1, false));
    }

    appBarSections.push({
      id: section.id || ("section-" + section.title),
      title: section.title,
      icon: navIconName(section.icon),
      open: section.active === true,
      spacerBefore: section.spacerBefore === true,
      spacerAfter: section.spacerAfter === true,
      class: "dashboard-nav-section",
      items: sectionItems
    });
  }

  var mobileMenuItems = navMobileMenuItems.map(function(item) {
    return toMobileMenuItem(item);
  });

  var footerContent = [];
  if (options.footer) footerContent.push(options.footer);
  if (appBarTailLinks.length > 0) {
    footerContent.push({
      n: "div",
      c: "dashboard-nav-tail-links d-flex flex-column gap-1",
      i: appBarTailLinks
    });
  }

  return {
    n: "ua.appbar",
    class: options.class || "dashboard-shell",
    sidebarClass: options.sidebarClass || "dashboard-sidebar",
    contentClass: options.contentClass || "dashboard-main",
    sidebarAttributes: options.sidebarAttributes || {},
    contentAttributes: options.contentAttributes || {},
    hideSpacer: options.hideSpacer !== false,
    mobileMenu: options.mobileMenu === true,
    singleOpen: options.singleOpen !== false,
    rememberOpenSection: options.rememberOpenSection !== false,
    openSectionStorageKey: (options.openSectionStorageKey || "dashboard.sidebar.open.section").toString(),
    header: options.header,
    footer: footerContent.length > 0 ? footerContent : undefined,
    items: appBarItems,
    sections: appBarSections,
    inner: options.inner,
    mobileMenuItems: mobileMenuItems
  };
}

exports.render = async function(options) {
  var opts = options || {};
  var providedNavigation = (opts.navigation && typeof opts.navigation === "object") ? opts.navigation : {};

  var cachedNavigation = readCachedNavigation(opts);
  var navigation = cachedNavigation || providedNavigation;
  if (!(cachedNavigation)) {
    writeCachedNavigation(opts, providedNavigation);
  }

  var appbarOptions = buildAppbarOptions(opts, navigation);
  delete appbarOptions.mobileMenuItems;

  return appbarOptions;
};
